import React, { useState } from 'react';
import { styled } from '@mui/material/styles';
import { TextField, IconButton, InputAdornment } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';

// Custom styled TextField component for regular text inputs
// Pass size="small" for 40px toolbar/filter height; default (omit size) is 56px for forms.
export const CustomTextField = styled(TextField)(({ theme, multiline, size }) => ({
    width: '100% !important',
    minWidth: '100%',
    '& .MuiOutlinedInput-root': {
        backgroundColor: 'white',
        borderRadius: 4,
        height: multiline ? 'auto' : (size === 'small' ? '40px' : '56px'),
        minHeight: multiline ? 'auto' : (size === 'small' ? '40px' : '56px'),
        border: `1px solid ${theme.palette.divider} !important`,
        overflow: 'hidden',
        position: 'relative',
        width: '100%',
        '&:hover': {
            borderColor: theme.palette.primary.main + ' !important',
        },
        '&.Mui-focused': {
            borderColor: theme.palette.primary.main + ' !important',
            borderWidth: '2px !important',
        },
        '&.Mui-error': {
            borderColor: theme.palette.error.main + ' !important',
            borderWidth: '2px !important',
        },
        '& fieldset': {
            border: 'none !important',
            boxShadow: 'none !important',
        },
        '&:hover fieldset': {
            border: 'none !important',
        },
        '&.Mui-focused fieldset': {
            border: 'none !important',
        },
        '&.Mui-error fieldset': {
            border: 'none !important',
        },
    },
    '& .MuiOutlinedInput-notchedOutline': {
        border: 'none !important',
        '& fieldset': {
            border: 'none !important',
        },
    },
    '& .MuiOutlinedInput-input': {
        backgroundColor: 'white',
        border: 'none',
        outline: 'none',
        paddingTop: multiline ? '8px' : (size === 'small' ? '8px' : '16px'),
        paddingBottom: multiline ? '8px' : (size === 'small' ? '8px' : '16px'),
        paddingLeft: '12px',
        paddingRight: '8px',
        flex: 1,
        boxSizing: 'border-box',
        color: theme.palette.text.primary,
        '&::placeholder': {
            color: theme.palette.text.secondary,
            opacity: 0.8,
        },
        // Override browser autofill background
        '&:-webkit-autofill': {
            WebkitBoxShadow: '0 0 0 1000px white inset !important',
            WebkitTextFillColor: `${theme.palette.text.primary} !important`,
            'transition': 'background-color 5000s ease-in-out 0s',
        },
        '&:-webkit-autofill:hover': {
            WebkitBoxShadow: '0 0 0 1000px white inset !important',
        },
        '&:-webkit-autofill:focus': {
            WebkitBoxShadow: '0 0 0 1000px white inset !important',
        },
        '&:-webkit-autofill:active': {
            WebkitBoxShadow: '0 0 0 1000px white inset !important',
        },
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
        border: 'none !important',
    },
    '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
        border: 'none !important',
    },
    '& .MuiInputLabel-root': {
        transform: size === 'small' ? 'translate(14px, 10px) scale(1)' : 'translate(14px, 16px) scale(1)',
        backgroundColor: 'transparent',
        paddingLeft: '4px',
        paddingRight: '4px',
        color: theme.palette.text.secondary,
        '&.Mui-focused': {
            transform: 'translate(14px, -6px) scale(0.75)',
            color: theme.palette.primary.main,
            backgroundColor: 'white',
        },
        '&.Mui-error': {
            color: theme.palette.error.main,
        },
        '&.MuiFormLabel-filled': {
            transform: 'translate(14px, -6px) scale(0.75)',
            backgroundColor: 'white',
        },
    },
    '& .MuiInputLabel-shrink': {
        transform: 'translate(14px, -6px) scale(0.75)',
        backgroundColor: 'white',
        zIndex: 1,
    },
    '& .MuiFormHelperText-root': {
        marginLeft: '14px',
        marginTop: '4px',
        fontSize: '0.75rem',
        display: 'block !important',
        visibility: 'visible !important',
        '&.Mui-error': {
            color: theme.palette.error.main + ' !important',
        },
    },
}));

// Custom styled TextField component for select fields
export const CustomSelectField = styled(TextField)(({ theme }) => ({
    width: '100% !important',
    minWidth: '100%',
    '& .MuiOutlinedInput-root': {
        backgroundColor: 'white',
        borderRadius: 4,
        height: '56px',
        border: `1px solid ${theme.palette.divider} !important`,
        overflow: 'hidden',
        position: 'relative',
        width: '100%',
        '&:hover': {
            borderColor: theme.palette.primary.main + ' !important',
        },
        '&.Mui-focused': {
            borderColor: theme.palette.primary.main + ' !important',
            borderWidth: '2px !important',
        },
        '& fieldset': {
            border: 'none !important',
            boxShadow: 'none !important',
        },
        '&:hover fieldset': {
            border: 'none !important',
        },
        '&.Mui-focused fieldset': {
            border: 'none !important',
        },
    },
    '& .MuiOutlinedInput-notchedOutline': {
        border: 'none !important',
        '& fieldset': {
            border: 'none !important',
        },
    },
    '& .MuiOutlinedInput-input': {
        backgroundColor: 'white',
        border: 'none',
        outline: 'none',
        paddingTop: '16px',
        paddingBottom: '16px',
        paddingLeft: '16px',
        paddingRight: '40px',
        width: '100%',
        boxSizing: 'border-box',
        color: theme.palette.text.primary,
        '&::placeholder': {
            color: theme.palette.text.secondary,
            opacity: 0.8,
        },
        // Override browser autofill background
        '&:-webkit-autofill': {
            WebkitBoxShadow: '0 0 0 1000px white inset !important',
            WebkitTextFillColor: `${theme.palette.text.primary} !important`,
            'transition': 'background-color 5000s ease-in-out 0s',
        },
        '&:-webkit-autofill:hover': {
            WebkitBoxShadow: '0 0 0 1000px white inset !important',
        },
        '&:-webkit-autofill:focus': {
            WebkitBoxShadow: '0 0 0 1000px white inset !important',
        },
        '&:-webkit-autofill:active': {
            WebkitBoxShadow: '0 0 0 1000px white inset !important',
        },
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
        border: 'none !important',
    },
    '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
        border: 'none !important',
    },
    '& .MuiInputLabel-root': {
        transform: 'translate(14px, 16px) scale(1)',
        backgroundColor: 'transparent',
        paddingLeft: '4px',
        paddingRight: '4px',
        color: theme.palette.text.secondary,
        '&.Mui-focused': {
            transform: 'translate(14px, -6px) scale(0.75)',
            color: theme.palette.primary.main,
            backgroundColor: 'white',
        },
        '&.MuiFormLabel-filled': {
            transform: 'translate(14px, -6px) scale(0.75)',
            backgroundColor: 'white',
        },
    },
    '& .MuiInputLabel-shrink': {
        transform: 'translate(14px, -6px) scale(0.75)',
        backgroundColor: 'white',
        zIndex: 1,
    },
    '& .MuiSelect-icon': {
        color: theme.palette.text.secondary,
        position: 'absolute',
        right: '12px',
        top: '50%',
        transform: 'translateY(-50%)',
    },
    '& .MuiSelect-select': {
        backgroundColor: 'white',
        border: 'none',
        width: '100%',
        boxSizing: 'border-box',
        paddingTop: '16px !important',
        paddingBottom: '16px !important',
        paddingLeft: '16px !important',
        paddingRight: '40px !important',
    },
    '& .MuiSelect-select.MuiSelect-select': {
        paddingTop: '16px !important',
        paddingBottom: '16px !important',
        paddingLeft: '16px !important',
        paddingRight: '40px !important',
    },
}));

// Custom styled TextField component for password fields
const StyledPasswordField = styled(TextField)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
        backgroundColor: 'white',
        borderRadius: 4,
        height: '56px',
        border: `1px solid ${theme.palette.divider} !important`,
        overflow: 'hidden',
        position: 'relative',
        '&:hover': {
            borderColor: theme.palette.primary.main + ' !important',
        },
        '&.Mui-focused': {
            borderColor: theme.palette.primary.main + ' !important',
            borderWidth: '2px !important',
        },
        '& fieldset': {
            border: 'none !important',
            boxShadow: 'none !important',
        },
        '&:hover fieldset': {
            border: 'none !important',
        },
        '&.Mui-focused fieldset': {
            border: 'none !important',
        },
    },
    '& .MuiOutlinedInput-notchedOutline': {
        border: 'none !important',
        '& fieldset': {
            border: 'none !important',
        },
    },
    '& .MuiOutlinedInput-input': {
        backgroundColor: 'white',
        border: 'none',
        outline: 'none',
        paddingTop: '16px',
        paddingBottom: '16px',
        paddingLeft: '16px',
        paddingRight: '40px',
        width: '100%',
        boxSizing: 'border-box',
        color: theme.palette.text.primary,
        '&::placeholder': {
            color: theme.palette.text.secondary,
            opacity: 0.8,
        },
        // Override browser autofill background
        '&:-webkit-autofill': {
            WebkitBoxShadow: '0 0 0 1000px white inset !important',
            WebkitTextFillColor: `${theme.palette.text.primary} !important`,
            'transition': 'background-color 5000s ease-in-out 0s',
        },
        '&:-webkit-autofill:hover': {
            WebkitBoxShadow: '0 0 0 1000px white inset !important',
        },
        '&:-webkit-autofill:focus': {
            WebkitBoxShadow: '0 0 0 1000px white inset !important',
        },
        '&:-webkit-autofill:active': {
            WebkitBoxShadow: '0 0 0 1000px white inset !important',
        },
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
        border: 'none !important',
    },
    '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
        border: 'none !important',
    },
    '& .MuiInputLabel-root': {
        transform: 'translate(14px, 16px) scale(1)',
        backgroundColor: 'transparent',
        paddingLeft: '4px',
        paddingRight: '4px',
        color: theme.palette.text.secondary,
        '&.Mui-focused': {
            transform: 'translate(14px, -6px) scale(0.75)',
            color: theme.palette.primary.main,
            backgroundColor: 'white',
        },
        '&.MuiFormLabel-filled': {
            transform: 'translate(14px, -6px) scale(0.75)',
            backgroundColor: 'white',
        },
    },
    '& .MuiInputLabel-shrink': {
        transform: 'translate(14px, -6px) scale(0.75)',
        backgroundColor: 'white',
        zIndex: 1,
    },
    '& .MuiIconButton-root': {
        color: `${theme.palette.action.disabled} !important`,
        position: 'absolute !important',
        right: '8px !important',
        top: '16px !important',
        transform: 'none !important',
        zIndex: 1,
        width: '20px !important',
        height: '20px !important',
        padding: '2px !important',
        margin: '0 !important',
    },
    '& .MuiInputAdornment-root': {
        backgroundColor: 'transparent !important',
        border: 'none !important',
        position: 'absolute !important',
        right: '0 !important',
        top: '0 !important',
        height: '100% !important',
        display: 'flex !important',
        alignItems: 'center !important',
        justifyContent: 'center !important',
        margin: '0 !important',
        padding: '0 !important',
    },
    '& .MuiInputAdornment-positionEnd': {
        position: 'absolute !important',
        right: '0 !important',
        top: '0 !important',
        height: '100% !important',
        display: 'flex !important',
        alignItems: 'center !important',
        justifyContent: 'center !important',
        margin: '0 !important',
        padding: '0 !important',
    },
    '& .MuiInputAdornment-outlined': {
        position: 'absolute !important',
        right: '0 !important',
        top: '0 !important',
        height: '100% !important',
        display: 'flex !important',
        alignItems: 'center !important',
        justifyContent: 'center !important',
        margin: '0 !important',
        padding: '0 !important',
    },
    '& .MuiInputAdornment-sizeMedium': {
        position: 'absolute !important',
        right: '0 !important',
        top: '0 !important',
        height: '100% !important',
        display: 'flex !important',
        alignItems: 'center !important',
        justifyContent: 'center !important',
        margin: '0 !important',
        padding: '0 !important',
    },
    '& .MuiIconButton-edgeEnd': {
        position: 'absolute !important',
        right: '8px !important',
        top: '16px !important',
        transform: 'none !important',
        margin: '0 !important',
    },
    '& .MuiIconButton-sizeMedium': {
        width: '24px !important',
        height: '24px !important',
        padding: '4px !important',
        margin: '0 !important',
    },
}));

// Custom Password Field Component with visibility toggle
export const CustomPasswordField = React.forwardRef((props, ref) => {
    const [showPassword, setShowPassword] = useState(false);

    const handleClickShowPassword = () => {
        setShowPassword(!showPassword);
    };

    const handleMouseDownPassword = (event) => {
        event.preventDefault();
    };

    return (
        <StyledPasswordField
            {...props}
            ref={ref}
            type={showPassword ? 'text' : 'password'}
            InputProps={{
                endAdornment: (
                    <InputAdornment position="end">
                        <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleClickShowPassword}
                            onMouseDown={handleMouseDownPassword}
                            edge="end"
                        >
                            {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                    </InputAdornment>
                ),
            }}
        />
    );
}); 